import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"
import { cloneElement, isValidElement, type ReactNode } from "react"

const OPTION_BASE =
  "group flex w-full items-start gap-3 rounded-2xl border p-4 transition cursor-pointer select-none"
const OPTION_INTERACTIONS = "hover:shadow-sm focus-within:ring-2 focus-within:ring-offset-2"

const optionLabelClass = (isActive: boolean) =>
  // cn(OPTION_BASE, OPTION_INTERACTIONS, isActive ? "border-primary/60 bg-primary/5" : "border-muted")
  cn(OPTION_BASE, OPTION_INTERACTIONS, isActive ? "border-primary/60 bg-primary/5" : "border-primary/10")

const iconClass = (isActive: boolean) =>
  cn("object-contain h-8 w-8 shrink-0", isActive ? "opacity-100" : "opacity-70")

export type ChoiceOption = {
  value: string
  title: string
  description?: string
  icon?: ReactNode
}

type ChoiceCardProps = {
  id: string
  option: ChoiceOption
  isActive: boolean
  children: ReactNode
  className?: string
}

type OptionContentProps = Pick<ChoiceOption, "title" | "description" | "icon"> & {
  isActive: boolean
  descId?: string
}

const OptionContent = ({ title, description, icon, isActive, descId }: OptionContentProps) => {

  const renderIcon = () => {
    if (!isValidElement(icon))
      return <span className={iconClass(isActive)}>{icon}</span>
    return cloneElement(icon, { className: cn(icon.props.className, iconClass(isActive)) })
  }

  return (
    <div className="flex-1 flex gap-4">
      {icon ? renderIcon() : <></>}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <span className="font-medium">{title}</span>
        </div>
        {description ? (
          <p id={descId} className="text-xs text-muted-foreground">
            {description}
          </p>
        ) : null}
      </div>
    </div>
  )
}

export const ChoiceCard = ({ id, option, isActive, children, className }: ChoiceCardProps) => {
  const { description } = option
  const descId = description ? `${id}-desc` : undefined

  return (
    <Label htmlFor={id} aria-describedby={descId} className={cn(optionLabelClass(isActive), className)}>
      {children}
      <OptionContent
        title={option.title}
        description={description}
        icon={option.icon}
        isActive={isActive}
        descId={descId}
      />
    </Label>
  )
}
